import React, { useState, useEffect, useMemo } from "react";
import { db } from "@/api/db";
import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { Radio, ArrowLeft, Clock, Calendar as CalendarIcon, X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { STATUS_CONFIG, CATEGORY_COLORS } from "@/lib/live-constants";

function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return url;
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

export default function EventCalendar() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [playerLesson, setPlayerLesson] = useState(null);

  useEffect(() => {
    db.entities.Lesson.filter({ type: "live", is_published: true }, "live_date")
      .then(data => { setLessons(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Map of "YYYY-MM-DD" -> array of lessons for dot rendering
  const lessonsByDate = useMemo(() => {
    const map = {};
    lessons.forEach(l => {
      if (!l.live_date) return;
      const d = new Date(l.live_date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });
    return map;
  }, [lessons]);

  // Lessons filtered by current month (and optionally by selected date)
  const visibleLessons = useMemo(() => {
    return lessons.filter(l => {
      if (!l.live_date) return false;
      const d = new Date(l.live_date);
      const inMonth = d.getFullYear() === viewMonth.getFullYear() && d.getMonth() === viewMonth.getMonth();
      if (!inMonth) return false;
      if (selectedDate) return isSameDay(d, selectedDate);
      return true;
    });
  }, [lessons, viewMonth, selectedDate]);

  // ── Player view ──
  if (playerLesson) {
    const statusKey = playerLesson.live_status || "upcoming";
    const sc = STATUS_CONFIG[statusKey];
    const catColor = CATEGORY_COLORS[playerLesson.category] || { bg: "#F2F4F4", text: "#646C6F" };
    const activeUrl = statusKey === "recording"
      ? (playerLesson.video_url || playerLesson.live_url)
      : playerLesson.live_url;
    const embedUrl = getEmbedUrl(activeUrl);

    return (
      <div className="max-w-4xl mx-auto space-y-5">
        <button
          onClick={() => setPlayerLesson(null)}
          className="flex items-center gap-1.5 text-sm font-medium"
          style={{ color: "#646C6F" }}
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para o calendário
        </button>

        <div className="rounded-2xl overflow-hidden shadow-md" style={{ background: "#1A1D1E" }}>
          <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0"
              style={{ background: sc.bg, color: sc.text }}
            >
              {statusKey === "live" && (
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
              )}
              {sc.label}
            </span>
            {playerLesson.category && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: catColor.bg, color: catColor.text }}
              >
                {playerLesson.category}
              </span>
            )}
            <p className="text-white text-sm font-medium truncate">{playerLesson.title}</p>
          </div>

          {embedUrl ? (
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                key={embedUrl}
                src={embedUrl}
                title={playerLesson.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#FEEDB4" }}>
                <Clock className="w-8 h-8" style={{ color: "#65541B" }} />
              </div>
              <p className="text-sm" style={{ color: "#99A6AB" }}>
                Encontro ainda não aconteceu — fique atento!
              </p>
            </div>
          )}
        </div>

        <div className="rounded-2xl p-5 space-y-2" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
          <h2 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#2E3233" }}>
            {playerLesson.title}
          </h2>
          {playerLesson.live_date && (
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#99A6AB" }} />
              <span className="text-sm" style={{ color: "#646C6F" }}>
                {new Date(playerLesson.live_date).toLocaleString("pt-BR", {
                  day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </div>
          )}
          {playerLesson.description && (
            <p className="text-sm leading-relaxed" style={{ color: "#646C6F" }}>
              {playerLesson.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Calendar + list view ──
  const monthLabel = viewMonth.toLocaleString("pt-BR", { month: "long", year: "numeric" });

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Calendário
        </h1>
        <p className="text-sm mt-0.5" style={{ color: "#99A6AB" }}>
          Visualize todos os encontros agendados no mês.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Calendar panel */}
          <div className="flex-shrink-0 lg:w-[380px] rounded-2xl p-5 self-start" style={{ background: "#fff", border: "1px solid #E5E9EA", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <style>{`
              .portus-cal { font-family: 'Inter', 'Poppins', sans-serif; }
              .portus-cal .rdp-months { width: 100%; }
              .portus-cal .rdp-month { width: 100%; }
              .portus-cal .rdp-caption {
                display: flex; align-items: center; justify-content: space-between;
                padding: 0 4px 16px; margin-bottom: 4px;
                border-bottom: 1px solid #E5E9EA;
              }
              .portus-cal .rdp-caption_label {
                font-size: 15px; font-weight: 700; color: #003326;
                text-transform: capitalize;
              }
              .portus-cal .rdp-nav { display: flex; gap: 4px; }
              .portus-cal .rdp-nav_button {
                width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
                border-radius: 10px; border: 1px solid #E5E9EA; background: #F9FAFA;
                color: #646C6F; cursor: pointer; transition: all 0.15s;
              }
              .portus-cal .rdp-nav_button:hover {
                background: #EAFAF6; border-color: #00DCA4; color: #00664C;
              }
              .portus-cal .rdp-table { width: 100%; border-collapse: separate; border-spacing: 0 4px; }
              .portus-cal .rdp-head_cell {
                font-size: 11px; font-weight: 600; color: #99A6AB; text-transform: uppercase;
                padding-bottom: 8px; width: calc(100% / 7); text-align: center;
              }
              .portus-cal .rdp-cell { text-align: center; padding: 0; }
              .portus-cal .rdp-day {
                width: 44px; height: 48px; display: inline-flex; flex-direction: column;
                align-items: center; justify-content: center; gap: 2px;
                border-radius: 12px; font-size: 14px; font-weight: 500; color: #2E3233;
                cursor: pointer; transition: all 0.15s; border: 2px solid transparent;
                background: transparent; position: relative;
              }
              .portus-cal .rdp-day:hover:not([aria-selected="true"]):not(.rdp-day_outside) {
                background: #F2F4F4; border-color: #E5E9EA;
              }
              .portus-cal .rdp-day_today:not([aria-selected="true"]) {
                background: #EAFAF6; color: #00664C; font-weight: 700;
                border-color: #B0FCEA;
              }
              .portus-cal .rdp-day[aria-selected="true"] {
                background: #00DCA4 !important; color: #003326 !important;
                font-weight: 700; border-color: #008572;
                box-shadow: 0 2px 8px rgba(0,220,164,0.3);
              }
              .portus-cal .rdp-day[aria-selected="true"] .cal-dot {
                background: #003326 !important;
              }
              .portus-cal .rdp-day_outside { color: #D9DBDA; pointer-events: none; }
              .portus-cal .rdp-day_outside .cal-dot { display: none; }
            `}</style>
            <DayPicker
              className="portus-cal"
              mode="single"
              selected={selectedDate}
              onSelect={(date) => setSelectedDate(date || null)}
              month={viewMonth}
              onMonthChange={setViewMonth}
              locale={ptBR}
              showOutsideDays={false}
              components={{
                IconLeft: () => <ChevronLeft className="w-4 h-4" />,
                IconRight: () => <ChevronRight className="w-4 h-4" />,
                DayContent: ({ date }) => {
                  const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                  const dayLessons = lessonsByDate[key] || [];
                  const dots = dayLessons.slice(0, 3);
                  return (
                    <>
                      <span>{date.getDate()}</span>
                      {dots.length > 0 && (
                        <div className="flex gap-[3px]" style={{ marginTop: -1 }}>
                          {dots.map((l, i) => {
                            const cc = CATEGORY_COLORS[l.category];
                            const color = cc ? cc.bg : "#00DCA4";
                            return (
                              <span
                                key={i}
                                className="cal-dot"
                                style={{
                                  width: 6, height: 6, borderRadius: "50%",
                                  background: color,
                                  boxShadow: `0 0 0 1px ${cc ? cc.text : "#008572"}`,
                                }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                },
              }}
            />

            {/* Legend */}
            <div className="mt-3 pt-3 flex flex-wrap gap-x-4 gap-y-1.5" style={{ borderTop: "1px solid #E5E9EA" }}>
              {Object.entries(CATEGORY_COLORS).slice(0, 6).map(([name, cc]) => (
                <div key={name} className="flex items-center gap-1.5">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: cc.bg, boxShadow: `0 0 0 1px ${cc.text}` }} />
                  <span className="text-[10px] font-medium" style={{ color: "#99A6AB" }}>{name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Event list panel */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* List header */}
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: "#2E3233" }}>
                {selectedDate
                  ? selectedDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                  : `Todos em ${monthLabel}`}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: "#99A6AB" }}>
                  {visibleLessons.length} {visibleLessons.length === 1 ? "encontro" : "encontros"}
                </span>
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ background: "#EAFAF6", color: "#00664C" }}
                  >
                    <X className="w-3 h-3" /> Ver todos do mês
                  </button>
                )}
              </div>
            </div>

            {/* Event cards — split into upcoming / past */}
            {(() => {
              const now = new Date();
              const upcoming = visibleLessons.filter(l => !l.live_date || new Date(l.live_date) >= now);
              const past = visibleLessons.filter(l => l.live_date && new Date(l.live_date) < now);

              if (visibleLessons.length === 0) {
                return (
                  <div className="rounded-2xl p-12 flex flex-col items-center gap-3 text-center"
                    style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
                    <CalendarIcon className="w-10 h-10 opacity-20" style={{ color: "#00664C" }} />
                    <p className="font-medium" style={{ color: "#99A6AB" }}>
                      {selectedDate ? "Nenhum encontro nesta data." : "Nenhum encontro neste mês."}
                    </p>
                  </div>
                );
              }

              const renderCard = (lesson, faded) => {
                const statusKey = lesson.live_status || "upcoming";
                const sc = STATUS_CONFIG[statusKey];
                const catColor = CATEGORY_COLORS[lesson.category] || null;
                const d = lesson.live_date ? new Date(lesson.live_date) : null;
                const timeStr = d ? d.toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : null;
                const dateStr = d ? d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : null;

                return (
                  <div
                    key={lesson.id}
                    onClick={() => setPlayerLesson(lesson)}
                    className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:shadow-sm"
                    style={{ background: "#fff", border: "1px solid #E5E9EA", opacity: faded ? 0.6 : 1 }}
                  >
                    {lesson.image_url ? (
                      <img
                        src={lesson.image_url}
                        alt={lesson.title}
                        className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: sc.gradient }}
                      >
                        <Radio className="w-6 h-6 text-white opacity-50" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: "#2E3233" }}>
                        {lesson.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {d && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: "#99A6AB" }}>
                            <CalendarIcon className="w-3 h-3" />
                            {dateStr} · {timeStr}
                          </span>
                        )}
                        {lesson.presenter && (
                          <span className="text-xs" style={{ color: "#99A6AB" }}>
                            · {lesson.presenter}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {catColor && lesson.category && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-semibold hidden sm:inline"
                          style={{ background: catColor.bg, color: catColor.text }}
                        >
                          {lesson.category}
                        </span>
                      )}
                      {faded && (lesson.video_url || lesson.live_status === "recording") ? (
                        <span
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                          style={{ background: "#EAFAF6", color: "#00664C" }}
                        >
                          <Play className="w-3 h-3" fill="#00664C" /> Assistir gravação
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{ background: sc.bg, color: sc.text }}
                        >
                          {statusKey === "live" && (
                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
                          )}
                          {sc.label}
                        </span>
                      )}
                    </div>
                  </div>
                );
              };

              return (
                <div className="space-y-5">
                  {upcoming.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#00664C" }}>
                        Próximas aulas
                      </p>
                      <div className="space-y-2">
                        {upcoming.map(l => renderCard(l, false))}
                      </div>
                    </div>
                  )}
                  {past.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#99A6AB" }}>
                        Já aconteceram
                      </p>
                      <div className="space-y-2">
                        {past.map(l => renderCard(l, true))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
